<template>
  <transition name="mc-intro-fade">
    <div v-if="visible" class="mc-intro-overlay">
      <div class="mc-intro-modal" role="dialog" aria-modal="true" aria-labelledby="mc-intro-title">
        <button type="button" class="mc-intro-close" aria-label="Tutup" @click="later">
          <i class="mdi mdi-close"></i>
        </button>

        <!-- Kiri: image slider -->
        <div class="mc-intro-showcase">
          <div class="mc-intro-slides">
            <transition name="mc-slide-fade" mode="out-in">
              <picture :key="activeSlide">
                <source :srcset="slides[activeSlide].webp" type="image/webp" />
                <img
                  :src="slides[activeSlide].png"
                  :alt="`MariChat preview ${activeSlide + 1}`"
                  class="mc-intro-slide-img"
                  loading="eager" />
              </picture>
            </transition>
          </div>
          <div class="mc-intro-dots">
            <button
              v-for="(s, i) in slides"
              :key="i"
              type="button"
              class="mc-intro-dot"
              :class="{ 'is-active': i === activeSlide }"
              :aria-label="`Slide ${i + 1}`"
              @click="goTo(i)"></button>
          </div>
        </div>

        <!-- Kanan: konten -->
        <div class="mc-intro-content">
          <div class="mc-intro-brand">
            <img src="/img/logo-marichat-black.svg" alt="MariChat" class="mc-intro-logo" />
          </div>

          <h3 id="mc-intro-title" class="mc-intro-title">Experience the New Look</h3>
          <p class="mc-intro-subtitle">
            Meet the WAPPIN fresh new look — <strong>MariChat</strong>. Jump right in, no login required.
          </p>

          <ul class="mc-intro-features">
            <li v-for="(f, i) in features" :key="i" class="mc-intro-feature">
              <span class="mc-intro-feature-icon">
                <i :class="f.icon"></i>
              </span>
              <div class="mc-intro-feature-text">
                <h6 class="mb-1">{{ f.title }}</h6>
                <p class="mb-0">{{ f.desc }}</p>
              </div>
            </li>
          </ul>

          <p class="mc-intro-subtitle">
            Available now! Full switch on <strong>September 1, 2026</strong>, when the old view will no longer be accessible.
          </p>

          <div class="mc-intro-actions">
            <button
              type="button"
              class="mc-intro-btn-primary"
              :disabled="loading"
              @click="tryNow">
              <i v-if="loading" class="mdi mdi-loading mdi-spin mr-1"></i>
              {{ loading ? 'Opening…' : 'Try Now' }}
            </button>
            <button type="button" class="mc-intro-btn-later" :disabled="loading" @click="later">
              Later
            </button>
          </div>
        </div>
      </div>
    </div>
  </transition>
</template>

<script>
import authAPI from '../../api/auth';

// Dismiss di-scope ke SESI login sekarang (bukan permanen): begitu login ulang (token
// baru) modal muncul lagi. Ini disengaja — tujuannya full replace, user harus selalu
// aware tiap sesi. sessionStorage juga hilang saat tab/browser ditutup.
const DISMISS_KEY = 'marichat_intro_dismissed_token';
const SLIDE_INTERVAL = 5000;

export default {
  name: 'MariChatIntroModal',
  data() {
    return {
      loading: false,
      dismissedToken: sessionStorage.getItem(DISMISS_KEY) || null,
      activeSlide: 0,
      slideTimer: null,
      slides: [
        { webp: '/img/marichat-showcase-01.webp', png: '/img/marichat-showcase-01.png' },
        { webp: '/img/marichat-showcase-02.webp', png: '/img/marichat-showcase-02.png' },
        { webp: '/img/marichat-showcase-03.webp', png: '/img/marichat-showcase-03.png' },
      ],
      features: [
        {
          icon: 'mdi mdi-view-dashboard-outline',
          title: 'Cleaner interface',
          desc: 'We\'ve redesigned the dashboard to helps you focus with a more organized and modern design',
        },
        {
          icon: 'mdi mdi-navigation',
          title: 'Enhanced Navigation',
          desc: 'Menus and key actions have been reorganized so you can reach what you need with fewer clicks',
        },
      ],
    };
  },
  computed: {
    currentToken() {
      return this.$store.getters['auth/token'] || null;
    },
    isLogin() {
      return this.$store.getters['auth/isLogin'];
    },
    visible() {
      // Tampil hanya kalau sudah login DAN belum dismiss di sesi (token) ini.
      if (!this.isLogin || !this.currentToken) return false;
      return this.dismissedToken !== this.currentToken;
    },
    logoSrc() {
      // Reuse aset yang ada; ganti ke logo MariChat kalau sudah tersedia.
      // eslint-disable-next-line global-require, import/no-unresolved
      return require('../../assets/images/logo-color.svg');
    },
  },
  watch: {
    visible(val) {
      if (val) this.startAutoplay();
      else this.stopAutoplay();
    },
  },
  mounted() {
    if (this.visible) this.startAutoplay();
  },
  beforeDestroy() {
    this.stopAutoplay();
  },
  methods: {
    tryNow() {
      if (this.loading) return;
      this.loading = true;
      authAPI.ssoMariChat().then((res) => {
        const redirectUrl = res.data && res.data.redirect_url;
        if (redirectUrl) {
          // Redirect penuh ke MariChat; /cobrand di sana menukar token jadi sesi.
          window.location.href = redirectUrl;
        } else {
          this.loading = false;
          this.$store.dispatch('ui/alertError', 'Gagal membuka MariChat. Coba lagi.');
        }
      }).catch(() => {
        this.loading = false;
        this.$store.dispatch('ui/alertError', 'Gagal membuka MariChat. Coba lagi.');
      });
    },
    later() {
      if (this.loading) return;
      // Tandai dismiss hanya untuk token sesi ini; login berikutnya modal balik lagi.
      this.dismissedToken = this.currentToken;
      if (this.currentToken) {
        sessionStorage.setItem(DISMISS_KEY, this.currentToken);
      }
    },
    goTo(i) {
      this.activeSlide = i;
      this.startAutoplay();
    },
    startAutoplay() {
      this.stopAutoplay();
      this.slideTimer = setInterval(() => {
        this.activeSlide = (this.activeSlide + 1) % this.slides.length;
      }, SLIDE_INTERVAL);
    },
    stopAutoplay() {
      if (this.slideTimer) {
        clearInterval(this.slideTimer);
        this.slideTimer = null;
      }
    },
  },
};
</script>

<style scoped>
.mc-intro-overlay {
  position: fixed;
  inset: 0;
  z-index: 3000;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 1rem;
  background: rgba(15, 18, 40, 0.55);
  backdrop-filter: blur(2px);
}

.mc-intro-modal {
  position: relative;
  display: flex;
  width: 100%;
  max-width: 860px;
  max-height: 92vh;
  overflow: hidden;
  background: #fff;
  border-radius: 18px;
  box-shadow: 0 24px 60px rgba(15, 18, 40, 0.28);
}

.mc-intro-close {
  position: absolute;
  top: 14px;
  right: 14px;
  z-index: 2;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  padding: 0;
  font-size: 20px;
  line-height: 1;
  color: #6b7280;
  background: rgba(255, 255, 255, 0.85);
  border: none;
  border-radius: 50%;
  cursor: pointer;
  transition: background 0.15s ease, color 0.15s ease;
}
.mc-intro-close:hover {
  color: #111827;
  background: #fff;
}

/* Kiri: showcase */
.mc-intro-showcase {
  position: relative;
  flex: 0 0 48%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 2rem 1.5rem;
  background: linear-gradient(150deg, #c6f24e 0%, #a8e63a 55%, #d7f77a 100%);
}

.mc-intro-slides {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;
}
.mc-intro-slide-img {
  max-width: 100%;
  max-height: 340px;
  border-radius: 10px;
  box-shadow: 0 18px 40px rgba(20, 40, 10, 0.22);
  object-fit: contain;
}

.mc-intro-dots {
  position: absolute;
  bottom: 18px;
  display: flex;
  gap: 8px;
}
.mc-intro-dot {
  width: 8px;
  height: 8px;
  padding: 0;
  background: rgba(31, 51, 10, 0.35);
  border: none;
  border-radius: 999px;
  cursor: pointer;
  transition: width 0.2s ease, background 0.2s ease;
}
.mc-intro-dot.is-active {
  width: 22px;
  background: #1f330a;
}

/* Kanan: konten */
.mc-intro-content {
  flex: 1 1 52%;
  display: flex;
  flex-direction: column;
  padding: 2.25rem 2rem;
  overflow-y: auto;
}

.mc-intro-brand {
  margin-bottom: 1.5rem;
}
.mc-intro-logo {
  height: 26px;
  width: auto;
}

.mc-intro-title {
  margin-bottom: 0.5rem;
  font-size: 1.6rem;
  font-weight: 700;
  color: #1f7a63;
}
.mc-intro-subtitle {
  margin-bottom: 1.5rem;
  font-size: 0.95rem;
  color: #4b5563;
}

.mc-intro-features {
  flex: 1 1 auto;
  margin: 0 0 0.5rem;
  padding: 0;
  list-style: none;
}
.mc-intro-feature {
  display: flex;
  gap: 0.85rem;
  margin-bottom: 1.15rem;
}
.mc-intro-feature-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  flex: 0 0 40px;
  height: 40px;
  font-size: 20px;
  color: #2fa587;
  background: rgba(47, 165, 135, 0.12);
  border-radius: 10px;
}
.mc-intro-feature-text h6 {
  font-size: 0.98rem;
  font-weight: 600;
  color: #111827;
}
.mc-intro-feature-text p {
  font-size: 0.86rem;
  color: #6b7280;
}

.mc-intro-actions {
  display: flex;
  align-items: center;
  gap: 1rem;
}
.mc-intro-btn-primary {
  padding: 0.6rem 1.6rem;
  font-size: 0.95rem;
  font-weight: 600;
  color: #fff;
  background: #2fa587;
  border: none;
  border-radius: 10px;
  cursor: pointer;
  transition: background 0.15s ease;
}
.mc-intro-btn-primary:hover:not(:disabled) {
  background: #268a71;
}
.mc-intro-btn-primary:disabled {
  opacity: 0.7;
  cursor: default;
}
.mc-intro-btn-later {
  padding: 0.6rem 0.5rem;
  font-size: 0.95rem;
  color: #6b7280;
  background: transparent;
  border: none;
  cursor: pointer;
}
.mc-intro-btn-later:hover:not(:disabled) {
  color: #374151;
}

/* Transitions */
.mc-intro-fade-enter-active,
.mc-intro-fade-leave-active {
  transition: opacity 0.2s ease;
}
.mc-intro-fade-enter,
.mc-intro-fade-leave-to {
  opacity: 0;
}
.mc-slide-fade-enter-active,
.mc-slide-fade-leave-active {
  transition: opacity 0.35s ease;
}
.mc-slide-fade-enter,
.mc-slide-fade-leave-to {
  opacity: 0;
}

/* Responsive: stack di layar kecil */
@media (max-width: 640px) {
  .mc-intro-modal {
    flex-direction: column;
    max-height: 94vh;
    overflow-y: auto;
  }
  .mc-intro-showcase {
    flex: 0 0 auto;
    padding: 1.5rem 1rem 2rem;
  }
  .mc-intro-slide-img {
    max-height: 200px;
  }
  .mc-intro-content {
    padding: 1.5rem;
  }
}
</style>
